/**
 * Checks if a referrer matches any URL in the provided array.
 *
 * @param {string} referrer - The referrer domain to check.
 * @param {Array} urls - URLs to match against.
 * @return {boolean} Whether the referrer matches any URL.
 */
const matchesAny = (referrer, urls) => {
  if (! urls.length) {
    // eslint-disable-next-line no-console
    console.debug('Social sites are empty for ad-referral-source.');
  }

  // Use forEach instead of for...of
  let isMatch = false;
  urls.forEach((url) => {
    if (referrer === url) {
      isMatch = true;
    }
  });
  return isMatch;
};

/**
 * Checks if a referrer includes any string in the provided array.
 *
 * @param {string} referrer - The referrer to check.
 * @param {Array} urls - URLs to match against.
 * @return {boolean} Whether the referrer includes any URL.
 */
const includesAny = (referrer, urls) => {
  if (! urls.length) {
    // eslint-disable-next-line no-console
    console.debug('Nexstar domains are empty for ad-referral-source.');
  }

  // Use some() instead of for...of
  return urls.some((url) => '' !== url && referrer.includes(url));
};

/**
 * Sets referral source cookie based on the user's origin.
 */
const setReferralSourceCookie = () => {
  // If setCookie doesn't exist, we can't do anything
  if (! window.setCookie) {
    return;
  }

  const { referrer } = document;
  const referralID = window.getCookie ? window.getCookie('referralId') : null;
  const params = (new URL(document.location)).searchParams;
  const referralVals = [
    'Paid',
    'Organic',
    'Social',
    'Newsletter',
    'Nexstar Referral',
    'Referral',
    'Direct',
    'Other',
  ];

  // If cookie is set to a valid referral source,
  // refresh its expiration date to one hour from now.
  if (referralVals.includes(referralID)) {
    window.setCookie('referralId', referralID);
    return;
  }

  let referrerDomain = '';
  if (referrer) {
    // Use array destructuring instead of direct array access
    const [, , domain] = referrer.split('/');
    referrerDomain = domain;
  }

  const utmSource = params.get('utm_source') ?? '';
  const utmMedium = params.get('utm_medium') ?? '';
  const socialSites = window.amScripts.ad_referral_source_inline.socialSites || []; // eslint-disable-line max-len
  const nxstDomains = window.amScripts.ad_referral_source_inline.nxstDomains || []; // eslint-disable-line max-len

  const isSocial = 'social' === utmMedium.toLowerCase() || matchesAny(referrerDomain, socialSites); // eslint-disable-line max-len
  const isPaid = includesAny(referrer, ['paid.outbrain.com']) || 'capital' === utmSource.toLowerCase() || params.has('gclid'); // eslint-disable-line max-len
  const isOrganic = includesAny(
    referrer,
    ['google', 'bing', 'yahoo', 'duckduckgo'],
  );
  const notNews = ! referrer.includes('news');
  const isNexstar = includesAny(referrer, nxstDomains);
  const isNewsletter = 'sailthru' === utmSource.toLowerCase();
  const isDirect = 'none' === referrer || '' === referrer;

  if (isPaid) {
    window.setCookie('referralId', 'Paid');
  } else if (isOrganic && notNews) {
    window.setCookie('referralId', 'Organic');
  } else if (isSocial) {
    window.setCookie('referralId', 'Social');
  } else if (isNewsletter) {
    window.setCookie('referralId', 'Newsletter');
  } else if (isNexstar) {
    window.setCookie('referralId', 'Nexstar Referral');
  } else if (referrer) {
    window.setCookie('referralId', 'Referral');
  } else if (isDirect) {
    window.setCookie('referralId', 'Direct');
  } else {
    window.setCookie('referralId', 'Other');
  }
};

setReferralSourceCookie();
